import { isSet, setLookupMappingFieldValue } from '../../../../Utils/CommonFunctions';
import { getUserInfo } from '../../../../Utils/UserFunctions';

/**
 * Process field data for a subform row item
 * @param {Object} item - Field item from section.data
 * @param {Object} rowData - Current row data
 * @param {Number} rowIndex - Row index
 * @param {String} rowId - Row ID
 * @returns {Object} Processed field data
 */
export const processFieldData = (item, rowData, rowIndex, rowId) => {
    const columnIndex = rowData.subform_column_values?.findIndex(
        (column) => column.table_descriptor_id == item.id
    ) ?? -1;
    
    let columnData = {};
    if (columnIndex !== -1) {
        columnData = rowData.subform_column_values[columnIndex];
    }
    
    let reminder = isSet(columnData.reminder, "");
    let files = isSet(columnData.files, "");
    let selectedValue = isSet(item.element, '') === 'CheckBoxes' 
        ? rowData.is_selected 
        : isSet(columnData.selected_value, "");
    let selected_value_text = isSet(columnData.selected_value_text, "");
    let selecteddpnm = isSet(columnData.dpnm, "");
    let overridden = isSet(columnData.overridden, false);
    let currency_code = isSet(columnData.currency_code, "CAD");
    let options = isSet(columnData.options, item.options);
    
    let elementObj = { element_original: item.element };
    if (['PickList', 'MultiSelect', 'User', 'Lookup', 'MultiLookup'].includes(item.element)) {
        elementObj = {
            element: 'CustomAll',
            element_original: item.element,
        };
    }
    if (['Image', 'File'].includes(item.element)) {
        elementObj = {
            element: 'CustomAllFiles',
            element_original: item.element,
        };
    }

    const updatedItem = {
        ...item,
        options: isSet(options, item.options),
        reminder,
        files,
        selectedValue,
        selectedValueText: selected_value_text,
        displayValue: selecteddpnm,
        overridden,
        id: `${item.id}_${rowIndex}_${rowId}`,
        modType: item.modType || 'ADDFORM',
        currency_code,
        ...elementObj
    };
    
    const fieldObj = {
        section_index: item.section_index || 0,
        itemIndex: item.itemIndex || 0,
        modType: item.modType || 'ADDFORM',
        item: updatedItem,
        rowIndex,
        subFormIndex: item.subFormIndex || 0,
        columnIndex
    };
    
    return {
        updatedItem,
        fieldObj,
        reminder,
        files,
        selectedValue,
        selected_value_text,
        selecteddpnm,
        overridden,
        currency_code,
        options,
        columnIndex,
        columnData
    };
};

/**
 * Get filtered section data based on modType
 * @param {Object} section - Section object
 * @param {String} modType - Module type (ADDFORM, DETAILVIEW, etc.)
 * @returns {Array} Filtered section data
 */
export const getSectionData = (section, modType) => {
    const userInfo = getUserInfo();
    let sectionData = isSet(section.data, []).map((item) => {
        if (modType === "DETAILVIEW" && item?.is_detail_view === false || 
            modType === "ADDFORM" && item?.is_visible === false) {
            item.access_mode = "HIDDEN";
        }
        return item;
    });
    
    // Filter for Community Partners role
    if (userInfo?.data?.role_name === 'Community Partners') {
        sectionData = sectionData.filter(
            (item) => item.api_custom_field_name !== "cf_quoted_cost"
        );
    }
    
    return sectionData;
};

/**
 * Find subform index by section ID
 * @param {Array} subformData - Array of subform data
 * @param {String|Number} sectionId - Section ID to find
 * @returns {Number} Subform index or -1 if not found
 */
export const getSubformIndex = (subformData, sectionId) => {
    return isSet(subformData, []).findIndex(function(subform) {
        return subform.field_group_section_id == sectionId;
    });
};

/**
 * Create a new subform row template (matching SubformTypeForm's addNewFormObject structure)
 * @param {Array} sectionData - Section data array (field definitions)
 * @param {Number} currentRowCount - Current number of rows (display_postition)
 * @returns {Object} New row object
 */
export const createNewSubformRow = (sectionData, currentRowCount = 0) => {
    // Create row matching SubformTypeForm's addNewFormObject structure exactly
    // In SubformTypeForm, selected_value[i] refers to section.data[i] (the field definition)
    // So we use sectionData[i].default_value directly
    return {
        id: "",
        display_postition: currentRowCount,
        is_selected: false,
        // Note: isOpenBox is NOT set initially in SubformTypeForm's addNewFormObject
        subform_column_values: sectionData.map((item, i) => {
            return {
                table_descriptor_id: item.id,
                display_name: item.label?.blocks?.[0]?.text || '',
                // Match SubformTypeForm: use default_value from field definition, empty for AutoNumber
                selected_value: (item.element === 'AutoNumber') ? '' : isSet(item.default_value, ''),
                overridden: false
                // Note: SubformTypeForm's addNewFormObject does NOT include:
                // selected_value_text, dpnm, reminder, files, currency_code, options
                // These are added later when fields are edited
            };
        })
    };
};

/**
 * Extract field value based on input type
 * @param {*} e - Event or value
 * @param {String} inputType - Input type (PickList, CheckBoxes, etc.)
 * @returns {*} Extracted field value
 */
export const extractFieldValue = (e, inputType = 'text') => {
    let fieldValue;
    
    if (inputType === 'PickList') {
        fieldValue = e;
    } else if (inputType === 'CheckBoxes') {
        fieldValue = e.target.checked;
    } else if (inputType === 'MultiSelect') {
        let MultiSelectValue = [];
        e = (e === null) ? [] : e;
        e.map((item) => {
            MultiSelectValue.push(item.id);
            return item;
        });
        fieldValue = MultiSelectValue.join(',');
    } else if (['Lookup', 'number', 'MultiLookup', 'date', 'Files', 'Reminder', 
                 'currencyType', 'exchangeRate', 'phoneNumber', 'CustomAllSingle'].includes(inputType)) {
        fieldValue = e;
    } else if (inputType === 'CustomAllArray') {
        let MultiArrayValue = [];
        e = (e === null) ? [] : e;
        e.map((item) => {
            MultiArrayValue.push(item.id);
            return item;
        });
        fieldValue = MultiArrayValue.join(',');
    } else if (e) {
        fieldValue = e.target ? e.target.value : e;
    }
    
    return fieldValue;
};

/**
 * Get column count for visible fields
 * @param {Array} sectionData - Section data array
 * @returns {Number} Column count
 */
export const getColumnCount = (sectionData) => {
    return sectionData.filter((item) => {
        return isSet(item.access_mode, 'EDITABLE') !== 'HIDDEN';
    }).length;
};

